import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Award, CheckCircle, ArrowRight, BookOpen, ClipboardCheck, FileText, ChevronRight, Download, User, Mail, MapPin, CreditCard } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { UniversalSurveyForm } from "@/components/UniversalSurveyForm";
import { CertificateDetailsForm } from "@/components/CertificateDetailsForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

export default function CECreditPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  // State
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  // Step: 'info' | 'register' | 'assessment' | 'survey' | 'complete'
  const [step, setStep] = useState('info');

  // Registration form
  const [regForm, setRegForm] = useState({ name: '', email: '', state: '', license_number: '', agd_number: '' });
  const [regLoading, setRegLoading] = useState(false);
  const [regMode, setRegMode] = useState('quick'); // 'quick' or 'full'
  const [fullPassword, setFullPassword] = useState('');

  // Assessment state
  const [assessment, setAssessment] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [submittingAssessment, setSubmittingAssessment] = useState(false);


  const colors = {
    bg: isDarkMode ? '#0a1628' : '#f8f9fa',
    cardBg: isDarkMode ? '#112240' : '#ffffff',
    heading: isDarkMode ? '#ffffff' : '#092036',
    text: isDarkMode ? '#94a3b8' : '#4b5563',
    accent: '#FF6B35',
    success: '#16a34a',
    border: isDarkMode ? '#1e3a5f' : '#e5e7eb',
  };

  // Load course
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await axios.get(`${API}/courses/public/${courseId}`);
        setCourse(res.data);
      } catch {
        toast.error("Course not found");
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();

    // Check existing session
    const existingToken = localStorage.getItem("session_token");
    if (existingToken) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${existingToken}` } })
        .then(res => {
          setUser(res.data);
          setToken(existingToken);
        })
        .catch(() => { /* no valid session */ });
    }
  }, [courseId]);

  // The actual course UUID (courseId from URL may be a slug)
  const realCourseId = course?.id || courseId;

  // Quick register
  const handleQuickRegister = async (e) => {
    e.preventDefault();
    if (!regForm.name || !regForm.email || !regForm.state.trim() || !regForm.license_number.trim()) {
      toast.error("Name, email, state and license number are required for CE credit");
      return;
    }
    setRegLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('name', regForm.name);
      formData.append('email', regForm.email);
      formData.append('state', regForm.state.trim().toUpperCase());
      formData.append('license_number', regForm.license_number.trim());
      if (regForm.agd_number.trim()) formData.append('agd_number', regForm.agd_number.trim());

      const res = await axios.post(`${API}/auth/quick-register`, formData);
      const { session_token, user: userData } = res.data;
      localStorage.setItem("session_token", session_token);
      setToken(session_token);
      setUser(userData);

      // Auto-enroll in course
      try {
        await axios.post(`${API}/courses/${realCourseId}/enroll`, {}, {
          headers: { Authorization: `Bearer ${session_token}` }
        });
      } catch { /* already enrolled or enrollment error */ }

      toast.success("You're in! Let's get your CE credit.");
      setStep('assessment');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setRegLoading(false);
    }
  };

  // Full register
  const handleFullRegister = async (e) => {
    e.preventDefault();
    if (!regForm.name || !regForm.email || !fullPassword || !regForm.state.trim() || !regForm.license_number.trim()) {
      toast.error("Name, email, password, state and license number are required");
      return;
    }
    setRegLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('name', regForm.name);
      formData.append('email', regForm.email);
      formData.append('password', fullPassword);
      formData.append('state', regForm.state.trim().toUpperCase());
      formData.append('license_number', regForm.license_number.trim());
      if (regForm.agd_number.trim()) formData.append('agd_number', regForm.agd_number.trim());

      const res = await axios.post(`${API}/auth/register`, formData);
      const { session_token, user: userData } = res.data;
      localStorage.setItem("session_token", session_token);
      setToken(session_token);
      setUser(userData);

      try {
        await axios.post(`${API}/courses/${realCourseId}/enroll`, {}, {
          headers: { Authorization: `Bearer ${session_token}` }
        });
      } catch { /* already enrolled */ }

      toast.success("Account created! Let's get your CE credit.");
      setStep('assessment');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setRegLoading(false);
    }
  };

  // Start CE flow for already-logged-in user
  const handleStartWithAccount = async () => {
    try {
      await axios.post(`${API}/courses/${realCourseId}/enroll`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* already enrolled */ }
    setStep('assessment');
  };

  // Load assessment
  const loadAssessment = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/courses/${realCourseId}/assessment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessment(res.data);
    } catch (error) {
      toast.error("Failed to load assessment");
    }
  }, [token, realCourseId]);

  useEffect(() => {
    if (step === 'assessment' && token) {
      loadAssessment();
    }
  }, [step, token, loadAssessment]);

  // Submit assessment
  const handleSubmitAssessment = async () => {
    if (!assessment) return;
    const questions = assessment.questions || [];
    const unanswered = questions.filter((_, i) => assessmentAnswers[i] === undefined);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${questions.length} questions`);
      return;
    }

    setSubmittingAssessment(true);
    try {
      const answers = questions.map((q, i) => ({
        question_idx: i,
        answer: assessmentAnswers[i],
        question: q
      }));

      const res = await axios.post(
        `${API}/courses/${realCourseId}/assessment`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAssessmentResult(res.data);

      if (res.data.passed) {
        toast.success(`Passed! Score: ${res.data.score}%`);
        // Load survey
        setStep('survey');
      } else {
        toast.error(`Score: ${res.data.score}%. You need ${course.passing_grade}% to pass. Try again!`);
        setAssessmentAnswers({});
        setAssessmentResult(null);
      }
    } catch (error) {
      toast.error("Failed to submit assessment");
    } finally {
      setSubmittingAssessment(false);
    }
  };

  // Download certificate
  const handleDownloadCertificate = async () => {
    try {
      const response = await fetch(`${API}/courses/${realCourseId}/certificate/download`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `CE_Certificate_${(course?.title || 'Course').replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        toast.success('Certificate downloaded!');
      } else {
        toast.error('Certificate not available yet.');
      }
    } catch {
      toast.error('Failed to download certificate.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.bg }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.accent }}></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.bg }}>
        <p style={{ color: colors.text }}>Course not found.</p>
      </div>
    );
  }

  // Progress steps
  const steps = [
    { key: 'info', label: 'Course Info', icon: BookOpen },
    { key: 'register', label: 'Your Info', icon: User },
    { key: 'assessment', label: 'Assessment', icon: ClipboardCheck },
    { key: 'survey', label: 'Survey', icon: FileText },
    { key: 'complete', label: 'Certificate', icon: Award },
  ];
  const currentStepIdx = steps.findIndex(s => s.key === step);

  return (
    <div className="min-h-screen" style={{ background: colors.bg }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: colors.border, background: colors.cardBg }}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-8 w-8 object-contain" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
            <span className="font-bold text-sm sm:text-base" style={{ color: colors.heading }}>Oral Surgery Success</span>
          </div>
          <span className="text-xs sm:text-sm font-medium px-3 py-1 rounded-full" style={{ background: isDarkMode ? '#1e3a5f' : '#fff7ed', color: colors.accent }}>
            Free CE Credit
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        {/* Progress Bar */}
        <div className="flex items-center justify-between mb-8" data-testid="ce-progress-bar">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === currentStepIdx;
            const isDone = i < currentStepIdx;
            return (
              <div key={s.key} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      background: isDone ? colors.success : isActive ? colors.accent : isDarkMode ? '#1e3a5f' : '#e5e7eb',
                      color: isDone || isActive ? '#ffffff' : colors.text
                    }}
                  >
                    {isDone ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
                  </div>
                  <span className="text-[10px] sm:text-xs mt-1 font-medium hidden sm:block" style={{ color: isActive ? colors.accent : colors.text }}>
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-1 sm:mx-2" style={{ background: isDone ? colors.success : isDarkMode ? '#1e3a5f' : '#e5e7eb' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step: Course Info */}
        {step === 'info' && (
          <Card className="p-6 sm:p-10" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ background: isDarkMode ? '#1e3a5f' : '#f0fdf4', color: colors.success }}>
                <Award className="w-4 h-4" />
                <span className="text-sm font-medium">{course.ce_hours || 1} CE Credit{(course.ce_hours || 1) !== 1 ? 's' : ''} Available</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: colors.heading }}>
                {course.title}
              </h1>
              <p className="text-base" style={{ color: colors.text }}>
                {course.description}
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <h3 className="font-semibold text-sm uppercase tracking-wide" style={{ color: colors.text }}>How it works</h3>
              {[
                { icon: BookOpen, text: "Watch the video on YouTube" },
                { icon: ClipboardCheck, text: `Pass the assessment (${course.passing_grade}% required)` },
                { icon: FileText, text: "Complete a short survey" },
                { icon: Award, text: "Download your CE certificate instantly" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isDarkMode ? '#1e3a5f' : '#f3f4f6' }}>
                    <item.icon className="w-4 h-4" style={{ color: colors.accent }} />
                  </div>
                  <span className="text-sm" style={{ color: colors.heading }}>{item.text}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => {
                if (user && token) {
                  handleStartWithAccount();
                } else {
                  setStep('register');
                }
              }}
              className="w-full py-6 text-lg font-semibold text-white"
              style={{ background: colors.accent }}
              data-testid="get-ce-credit-btn"
            >
              Get Your CE Credit <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Card>
        )}

        {/* Step: Register */}
        {step === 'register' && (
          <Card className="p-6 sm:p-8" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
            <h2 className="text-xl font-bold mb-1" style={{ color: colors.heading }}>Your Information</h2>
            <p className="text-sm mb-6" style={{ color: colors.text }}>We need a few details for your CE certificate.</p>

            {/* Toggle: Quick vs Full */}
            <div className="flex gap-2 mb-6 p-1 rounded-lg" style={{ background: isDarkMode ? '#0a1628' : '#f3f4f6' }}>
              <button
                onClick={() => setRegMode('quick')}
                className="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all"
                style={{
                  background: regMode === 'quick' ? colors.accent : 'transparent',
                  color: regMode === 'quick' ? '#ffffff' : colors.text
                }}
                data-testid="quick-register-tab"
              >
                Quick CE Credit
              </button>
              <button
                onClick={() => setRegMode('full')}
                className="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all"
                style={{
                  background: regMode === 'full' ? colors.accent : 'transparent',
                  color: regMode === 'full' ? '#ffffff' : colors.text
                }}
                data-testid="full-register-tab"
              >
                Create Full Account
              </button>
            </div>

            {regMode === 'quick' && (
              <p className="text-xs mb-4 px-3 py-2 rounded" style={{ background: isDarkMode ? '#1e3a5f' : '#f0fdf4', color: colors.success }}>
                No password needed. Just enter your info and take the assessment.
              </p>
            )}
            {regMode === 'full' && (
              <p className="text-xs mb-4 px-3 py-2 rounded" style={{ background: isDarkMode ? '#1e3a5f' : '#fff7ed', color: colors.accent }}>
                Create a full account to access your dashboard, track CE hours, and explore more courses.
              </p>
            )}

            <form onSubmit={regMode === 'quick' ? handleQuickRegister : handleFullRegister} className="space-y-4">
              <div>
                <Label style={{ color: colors.heading }}>Full Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: colors.text }} />
                  <Input
                    value={regForm.name}
                    onChange={e => setRegForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Dr. Jane Smith"
                    className="pl-10"
                    required
                    data-testid="ce-reg-name"
                  />
                </div>
              </div>

              <div>
                <Label style={{ color: colors.heading }}>Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: colors.text }} />
                  <Input
                    type="email"
                    value={regForm.email}
                    onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="jane@example.com"
                    className="pl-10"
                    required
                    data-testid="ce-reg-email"
                  />
                </div>
              </div>

              {regMode === 'full' && (
                <div>
                  <Label style={{ color: colors.heading }}>Password *</Label>
                  <Input
                    type="password"
                    value={fullPassword}
                    onChange={e => setFullPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    data-testid="ce-reg-password"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: colors.heading }}>State *</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: colors.text }} />
                    <Input
                      value={regForm.state}
                      onChange={e => setRegForm(f => ({ ...f, state: e.target.value }))}
                      placeholder="TN"
                      className="pl-10"
                      data-testid="ce-reg-state"
                    />
                  </div>
                </div>
                <div>
                  <Label style={{ color: colors.heading }}>License # *</Label>
                  <div className="relative mt-1">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: colors.text }} />
                    <Input
                      value={regForm.license_number}
                      onChange={e => setRegForm(f => ({ ...f, license_number: e.target.value }))}
                      placeholder="12345"
                      className="pl-10"
                      data-testid="ce-reg-license"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label style={{ color: colors.heading }}>AGD member # (optional)</Label>
                <Input
                  value={regForm.agd_number}
                  onChange={e => setRegForm(f => ({ ...f, agd_number: e.target.value }))}
                  placeholder="Leave blank if not an AGD member"
                  className="mt-1"
                  data-testid="ce-reg-agd"
                />
              </div>
              <p className="text-xs" style={{ color: colors.text }}>State and license number are printed on your CE certificate.</p>

              <Button
                type="submit"
                disabled={regLoading}
                className="w-full py-5 text-base font-semibold text-white"
                style={{ background: colors.accent }}
                data-testid="ce-reg-submit"
              >
                {regLoading ? 'Processing...' : 'Continue to Assessment'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </Card>
        )}

        {/* Step: Assessment */}
        {step === 'assessment' && (
          <Card className="p-6 sm:p-8" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
            <h2 className="text-xl font-bold mb-1" style={{ color: colors.heading }}>Assessment</h2>
            <p className="text-sm mb-6" style={{ color: colors.text }}>
              Score {course.passing_grade}% or higher to earn your CE credit.
            </p>

            {!assessment ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: colors.accent }}></div>
              </div>
            ) : (
              <div className="space-y-6">
                {(assessment.questions || []).map((q, idx) => (
                  <div key={idx} className="p-4 rounded-lg" style={{ background: isDarkMode ? '#0a1628' : '#f9fafb', border: `1px solid ${colors.border}` }}>
                    <p className="font-medium mb-3 text-sm" style={{ color: colors.heading }}>
                      {idx + 1}. {q.question}
                    </p>
                    <div className="space-y-2">
                      {(q.options || []).map((opt, optIdx) => (
                        <label
                          key={optIdx}
                          className="flex items-center gap-3 p-2.5 rounded-md cursor-pointer transition-colors text-sm"
                          style={{
                            background: assessmentAnswers[idx] === optIdx
                              ? (isDarkMode ? '#1e3a5f' : '#fff7ed')
                              : 'transparent',
                            border: `1px solid ${assessmentAnswers[idx] === optIdx ? colors.accent : 'transparent'}`
                          }}
                        >
                          <input
                            type="radio"
                            name={`q-${idx}`}
                            checked={assessmentAnswers[idx] === optIdx}
                            onChange={() => setAssessmentAnswers(a => ({ ...a, [idx]: optIdx }))}
                            className="accent-orange-500"
                          />
                          <span style={{ color: colors.heading }}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <Button
                  onClick={handleSubmitAssessment}
                  disabled={submittingAssessment}
                  className="w-full py-5 text-base font-semibold text-white"
                  style={{ background: colors.accent }}
                  data-testid="ce-submit-assessment"
                >
                  {submittingAssessment ? 'Grading...' : 'Submit Assessment'}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Step: Survey */}
        {step === 'survey' && (
          <Card className="p-6 sm:p-8" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
            {(!user?.state || !user?.license_number) ? (
              <>
                <h2 className="text-xl font-bold mb-4" style={{ color: colors.heading }}>Certificate Details</h2>
                <CertificateDetailsForm user={user} token={token} colors={colors} onSaved={(updated) => setUser(prev => ({ ...prev, ...updated }))} />
              </>
            ) : (
            <>
            <h2 className="text-xl font-bold mb-1" style={{ color: colors.heading }}>Course Evaluation Survey</h2>
            <p className="text-sm mb-6" style={{ color: colors.text }}>
              Almost done! Complete this survey to receive your certificate.
            </p>
            <UniversalSurveyForm
              courseId={realCourseId}
              token={token}
              isDarkMode={isDarkMode}
              defaultEmail={user?.email}
              onSubmitted={() => {
                toast.success("Survey submitted! Your certificate is ready.");
                setStep('complete');
              }}
            />
            </>
            )}
          </Card>
        )}

        {/* Step: Complete */}
        {step === 'complete' && (
          <div className="space-y-6">
            <Card className="p-6 sm:p-10 text-center" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#f0fdf4' }}>
                <Award className="w-8 h-8" style={{ color: colors.success }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: colors.heading }}>
                Congratulations!
              </h2>
              <p className="mb-6" style={{ color: colors.text }}>
                You've earned {course.ce_hours || 1} CE credit{(course.ce_hours || 1) !== 1 ? 's' : ''} for <strong>{course.title}</strong>.
                A copy will also be sent to your email.
              </p>

              <Button
                onClick={handleDownloadCertificate}
                className="py-5 px-8 text-base font-semibold text-white gap-2"
                style={{ background: colors.success }}
                data-testid="ce-download-cert"
              >
                <Download className="w-5 h-5" />
                Download Your Certificate
              </Button>
            </Card>

            {/* Upsell */}
            <Card className="p-6 sm:p-8" style={{ background: isDarkMode ? '#1a2e44' : '#fff7ed', border: `1px solid ${isDarkMode ? '#FF6B35' : '#fed7aa'}` }}>
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1" style={{ color: colors.heading }}>
                    Want more CE courses?
                  </h3>
                  <p className="text-sm" style={{ color: colors.text }}>
                    Get unlimited access to our full library of oral surgery CE courses for $129/year with the All Access Pass.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/pricing/ce-membership')}
                  className="text-white px-6 py-3 whitespace-nowrap gap-2"
                  style={{ background: colors.accent }}
                  data-testid="ce-upsell-btn"
                >
                  Explore All Access Pass <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
